import { buildEntityDeepLink } from '../../../shared/utils/entityDeepLink';
import { sortEventsByDateTime } from './calendar.helpers';

const ACTIVE_PAYMENT_STATUSES = new Set(['previsto', 'in_ritardo']);
const ACTIVE_EXPENSE_STATUSES = new Set(['da_saldare', 'previsto']);

function encodeRouteValue(value) {
  return encodeURIComponent(String(value));
}

function getItemLabel(item, fallback) {
  return item.title || item.name || item.assignment || item.description || item.client || item.supplierName || fallback;
}

function buildLinkedEntity({ type, id, label, moduleKey, route, actionLabel }) {
  if (!type || id === undefined || id === null || id === '') {
    return null;
  }

  return {
    type,
    id,
    label: label || `${type} ${id}`,
    moduleKey,
    route,
    actionLabel,
  };
}

export function markManualCalendarEvent(event) {
  return {
    ...event,
    source: event.source || 'calendar',
    sourceModule: event.sourceModule || 'calendar',
    isReadOnly: false,
  };
}

function buildReadOnlyEvent({
  id,
  title,
  description = '',
  date,
  type = 'deadline',
  priority = 'media',
  sourceModule,
  sourceLabel,
  status = '',
  linkedEntity = null,
  financialDetails = null,
  clientId = null,
  clientName = '',
  supplierId = null,
  supplierName = '',
}) {
  return {
    id,
    title,
    description,
    date,
    time: '',
    duration: 'Scadenza',
    type,
    attendees: [],
    location: sourceLabel || '',
    priority,
    status,
    source: 'derived',
    sourceModule,
    sourceLabel,
    linkedEntity,
    financialDetails,
    clientId,
    clientName,
    supplierId,
    supplierName,
    isReadOnly: true,
  };
}

function collectDealPaymentDates(deal) {
  return [
    deal.paymentExpectedDate,
    deal.paymentDate,
    deal.dataPagamentoPrevista,
    deal.dataPagamento,
    ...(deal.installmentDueDates || []),
    ...(deal.dilazionamento?.scadenze || []),
  ].filter(Boolean);
}

function buildDealPaymentEvents(deals = [], sourceModule, sourceLabel) {
  return deals.flatMap((deal) => {
    const dates = [...new Set(collectDealPaymentDates(deal))];
    const label = getItemLabel(deal, `Affare ${deal.id}`);
    const route =
      sourceModule === 'proposal'
        ? `/proposte-affari?dealId=${encodeRouteValue(deal.id)}`
        : sourceModule === 'in-progress'
          ? `/affari-in-corso?dealId=${encodeRouteValue(deal.id)}`
          : `/affari-completati?dealId=${encodeRouteValue(deal.id)}`;
    const linkedEntity = buildLinkedEntity({
      type:
        sourceModule === 'proposal'
          ? 'proposal'
          : sourceModule === 'in-progress'
            ? 'in-progress-deal'
            : 'completed-deal',
      id: deal.id,
      label,
      moduleKey: 'deals',
      route,
      actionLabel: 'Apri affare',
    });

    return dates.map((date) =>
      buildReadOnlyEvent({
        id: `deal-${sourceModule}:${deal.id}:${date}`,
        title: `Scadenza pagamento affare: ${label}`,
        description: deal.client ? `Cliente: ${deal.client}` : '',
        date,
        type: 'incasso',
        sourceModule: `deals-${sourceModule}`,
        sourceLabel,
        linkedEntity,
      })
    );
  });
}

export function buildDerivedCalendarEvents({
  assignments = [],
  payments = [],
  expenses = [],
  proposals = [],
  inProgressDeals = [],
  completedDeals = [],
}) {
  return [
    ...assignments
      .filter((assignment) => assignment.status === 'in_corso' && assignment.dueDate)
      .map((assignment) =>
        buildReadOnlyEvent({
          id: `assignment:${assignment.id}`,
          title: `Scadenza task: ${getItemLabel(assignment, `Task ${assignment.id}`)}`,
          description: assignment.description || '',
          date: assignment.dueDate,
          type: 'deadline',
          priority: assignment.urgency || 'media',
          sourceModule: 'assignments',
          sourceLabel: 'Task',
          status: assignment.status,
          linkedEntity: buildLinkedEntity({
            type: 'assignment',
            id: assignment.id,
            label: getItemLabel(assignment, `Task ${assignment.id}`),
            moduleKey: 'deals',
            route: `/incarichi?assignmentId=${encodeRouteValue(assignment.id)}`,
            actionLabel: 'Apri task',
          }),
        })
      ),
    ...payments
      .filter((payment) => ACTIVE_PAYMENT_STATUSES.has(payment.status) && payment.dueDate)
      .map((payment) =>
        buildReadOnlyEvent({
          id: `payment:${payment.id}`,
          title: `Incasso previsto: ${getItemLabel(payment, `Pagamento ${payment.id}`)}`,
          description: payment.invoiceNumber ? `Fattura ${payment.invoiceNumber}` : '',
          date: payment.dueDate,
          type: 'incasso',
          priority: payment.status === 'in_ritardo' ? 'alta' : 'media',
          sourceModule: 'accounting-income',
          sourceLabel: 'Incasso',
          status: payment.status,
          clientId: payment.clientId ?? null,
          clientName: payment.client || '',
          financialDetails: {
            kind: 'payment',
            counterpartyLabel: 'Pagatore / Cliente',
            counterpartyId: payment.clientId ?? null,
            counterpartyType: payment.clientId != null ? 'client' : null,
            counterpartyName: payment.client || '',
            amount: payment.amount,
            reference: payment.invoiceNumber || payment.assignment || `Pagamento ${payment.id}`,
            status: payment.status,
            dueDate: payment.dueDate,
            paymentDate: payment.paymentDate || '',
          },
          linkedEntity: buildLinkedEntity({
            type: 'payment',
            id: payment.id,
            label: getItemLabel(payment, `Pagamento ${payment.id}`),
            moduleKey: 'accounting',
            route: buildEntityDeepLink('/contabilita-aziendale', { entityType: 'payment', entityId: payment.id, legacyParam: 'paymentId' }),
            actionLabel: 'Apri incasso',
          }),
        })
      ),
    ...expenses
      .filter((expense) => ACTIVE_EXPENSE_STATUSES.has(expense.status) && expense.dueDate)
      .map((expense) =>
        buildReadOnlyEvent({
          id: `expense:${expense.id}`,
          title: `Uscita prevista: ${getItemLabel(expense, `Spesa ${expense.id}`)}`,
          description: expense.supplierName ? `Fornitore: ${expense.supplierName}` : '',
          date: expense.dueDate,
          type: 'pagamento',
          sourceModule: 'accounting-expenses',
          sourceLabel: 'Uscita',
          status: expense.status,
          supplierId: expense.supplierId ?? null,
          supplierName: expense.supplierName || '',
          financialDetails: {
            kind: 'expense',
            counterpartyLabel: 'Beneficiario / Destinatario',
            counterpartyId: expense.supplierId ?? null,
            counterpartyType: expense.supplierId != null ? 'supplier' : null,
            counterpartyName: expense.supplierName || expense.recipientName || '',
            amount: expense.amount,
            reference: expense.description || `Uscita ${expense.id}`,
            status: expense.status,
            dueDate: expense.dueDate,
            paymentDate: expense.paymentDate || '',
          },
          linkedEntity: buildLinkedEntity({
            type: 'expense',
            id: expense.id,
            label: getItemLabel(expense, `Spesa ${expense.id}`),
            moduleKey: 'accounting',
            route: buildEntityDeepLink('/uscite', { entityType: 'expense', entityId: expense.id, legacyParam: 'expenseId' }),
            actionLabel: 'Apri uscita',
          }),
        })
      ),
    ...buildDealPaymentEvents(proposals.filter((deal) => !deal.inProgressDealId), 'proposal', 'Proposta'),
    ...buildDealPaymentEvents(inProgressDeals.filter((deal) => !deal.completedDealId && !payments.some((payment) => String(payment.inProgressDealId) === String(deal.id))), 'in-progress', 'Affare in corso'),
    ...buildDealPaymentEvents(completedDeals.filter((deal) => !payments.some((payment) => String(payment.completedDealId) === String(deal.id))), 'completed', 'Affare completato'),
  ];
}

// Dashboard and Calendar use the same persisted occurrences and source links.
// Recurring expenses are generated by accounting; do not synthesize them again here.
export function buildCalendarEventCatalog({ manualEvents = [], ...sources }) {
  return sortEventsByDateTime([
    ...manualEvents.map(markManualCalendarEvent), ...buildDerivedCalendarEvents(sources),
  ]);
}
